<div class="legal-page">
  <h1>Accessibility Statement</h1>
  <p class="last-updated">Last Updated: November 11, 2025</p>

  <section>
    <h2>Our Commitment to Accessibility</h2>
    <p>
      We are committed to ensuring that this website and its applications are accessible to people with disabilities.
      We strive to conform to the <strong>Web Content Accessibility Guidelines (WCAG) 2.1 Level AA</strong> standards.
    </p>
    <p>
      Accessibility is an ongoing effort. We continuously work to improve the user experience for everyone and apply the relevant accessibility standards.
    </p>
  </section>

  <section>
    <h2>Accessibility Features</h2>
    <p>
      This website includes the following accessibility features:
    </p>

    <h3>Keyboard Navigation</h3>
    <ul>
      <li>✅ All interactive elements are keyboard accessible</li>
      <li>✅ Tab navigation follows logical order</li>
      <li>✅ Focus indicators are visible on all interactive elements</li>
      <li>✅ Keyboard shortcuts are available in some apps (e.g., Calculator)</li>
    </ul>

    <h3>Screen Reader Compatibility</h3>
    <ul>
      <li>✅ Semantic HTML elements for proper structure</li>
      <li>✅ ARIA labels on custom controls</li>
      <li>✅ Alternative text for meaningful images</li>
      <li>✅ Form labels properly associated with inputs</li>
    </ul>

    <h3>Visual Design</h3>
    <ul>
      <li>✅ High contrast color scheme</li>
      <li>✅ Text is resizable up to 200% without loss of functionality</li>
      <li>✅ Color is not used as the only means of conveying information</li>
      <li>✅ Consistent navigation and layout across pages</li>
    </ul>

    <h3>Content</h3>
    <ul>
      <li>✅ Clear, simple language</li>
      <li>✅ Descriptive link text</li>
      <li>✅ Helpful error messages with suggestions</li>
      <li>✅ Page titles and headings clearly describe topics</li>
    </ul>
  </section>

  <section>
    <h2>Known Accessibility Issues</h2>
    <p>
      Despite our best efforts, some limitations currently exist. We are actively working to address these:
    </p>

    <h3>Canvas-Based Tools</h3>
    <ul>
      <li>⚠️ <strong>Fill & Sign PDF:</strong> Canvas annotation tools (drawing, text placement) are not fully keyboard accessible</li>
      <li>⚠️ <strong>Face Blur:</strong> Manual brush tool requires mouse/touch input</li>
      <li><strong>Workaround:</strong> Face Blur offers an "Auto detect faces" button for keyboard users</li>
    </ul>

    <h3>Drag-and-Drop Interfaces</h3>
    <ul>
      <li>⚠️ Some file reordering features (PDF merge, Markdown to PDF) rely on drag-and-drop</li>
      <li><strong>Workaround:</strong> File order can be adjusted by re-uploading files in the desired order</li>
    </ul>

    <h3>Video Elements</h3>
    <ul>
      <li>⚠️ <strong>QR Scanner:</strong> Camera preview video lacks captions (live camera feed, no audio)</li>
      <li>⚠️ <strong>Audio/Video Tools:</strong> Trimmed video previews lack caption tracks</li>
    </ul>

    <h3>Color Contrast</h3>
    <ul>
      <li>⚠️ Some secondary text and muted labels may not meet 4.5:1 contrast ratio in all cases</li>
    </ul>
  </section>

  <section>
    <h2>Supported Technologies</h2>
    <p>
      This website is designed to work with the following assistive technologies:
    </p>
    <ul>
      <li><strong>Screen Readers:</strong> NVDA (Windows), JAWS (Windows), VoiceOver (macOS, iOS), TalkBack (Android)</li>
      <li><strong>Screen Magnification:</strong> ZoomText, Windows Magnifier, macOS Zoom</li>
      <li><strong>Voice Control:</strong> Dragon NaturallySpeaking, Voice Control (macOS/iOS)</li>
      <li><strong>Keyboard Navigation:</strong> Full keyboard support (no mouse required)</li>
    </ul>

    <h3>Browser Compatibility</h3>
    <p>
      For the best accessible experience, we recommend:
    </p>
    <ul>
      <li>Chrome 90+ (Windows, macOS, Linux)</li>
      <li>Firefox 90+ (Windows, macOS, Linux)</li>
      <li>Safari 14+ (macOS, iOS)</li>
      <li>Edge 90+ (Windows, macOS)</li>
    </ul>
  </section>

  <section>
    <h2>Alternative Formats & Methods</h2>

    <h3>File Processing</h3>
    <p>
      All tools process files locally in your browser. If you encounter accessibility barriers:
    </p>
    <ul>
      <li>Consider using desktop applications with better accessibility support</li>
      <li>Contact us for assistance or alternative solutions</li>
    </ul>

    <h3>Keyboard Shortcuts</h3>
    <p>
      <strong>Calculator App:</strong>
    </p>
    <ul>
      <li><kbd>0-9</kbd> - Number input</li>
      <li><kbd>+</kbd>, <kbd>-</kbd>, <kbd>*</kbd>, <kbd>/</kbd> - Operations</li>
      <li><kbd>Enter</kbd> - Calculate</li>
      <li><kbd>Escape</kbd> - Clear</li>
    </ul>

    <h3>Adjusting Text Size</h3>
    <p>
      You can adjust text size in your browser:
    </p>
    <ul>
      <li><strong>Windows/Linux:</strong> <kbd>Ctrl</kbd> + <kbd>+</kbd> or <kbd>Ctrl</kbd> + <kbd>-</kbd></li>
      <li><strong>macOS:</strong> <kbd>Cmd</kbd> + <kbd>+</kbd> or <kbd>Cmd</kbd> + <kbd>-</kbd></li>
      <li><strong>Reset:</strong> <kbd>Ctrl/Cmd</kbd> + <kbd>0</kbd></li>
    </ul>
  </section>

  <section>
    <h2>Third-Party Content</h2>
    <p>
      Some applications integrate with third-party services (Open-Meteo API, GitHub API). We cannot guarantee the accessibility of external content.
      If you encounter barriers with third-party services, please contact them directly.
    </p>
  </section>

  <section>
    <h2>Testing & Evaluation</h2>
    <p>
      This website has been evaluated using:
    </p>
    <ul>
      <li>Manual testing with screen readers (NVDA, VoiceOver)</li>
      <li>Keyboard-only navigation testing</li>
      <li>Automated tools (axe DevTools, Lighthouse)</li>
      <li>Color contrast analyzers</li>
    </ul>
    <p>
      <strong>Conformance Status:</strong> Partially Conformant
    </p>
    <p>
      "Partially conformant" means that some parts of the content do not fully conform to WCAG 2.1 Level AA.
      We are committed to addressing these gaps.
    </p>
  </section>

  <section>
    <h2>Feedback & Contact</h2>
    <p>
      We welcome your feedback on the accessibility of this website. If you encounter accessibility barriers, please let us know:
    </p>
    <ul>
      <li><strong>What page/app:</strong> Describe where you encountered the issue</li>
      <li><strong>What problem:</strong> Explain what happened and what you expected</li>
      <li><strong>Your setup:</strong> Browser, assistive technology, and operating system</li>
    </ul>
    <p>
      <strong>Contact Methods:</strong>
    </p>
    <ul>
      <li><a href="#/feedback">Send feedback through the website</a></li>
      <li>Email <a href="mailto:contact@spastudio.org">contact@spastudio.org</a></li>
    </ul>
    <p>
      We aim to respond to accessibility feedback within <strong>5 business days</strong> and to resolve issues within a reasonable timeframe depending on complexity.
    </p>
  </section>

  <section>
    <h2>Ongoing Improvements</h2>
    <p>
      We are actively working to improve accessibility, including:
    </p>
    <ul>
      <li>🔄 Adding keyboard alternatives for drag-and-drop interfaces</li>
      <li>🔄 Improving ARIA labels on all form controls</li>
      <li>🔄 Enhancing focus management in modal dialogs</li>
      <li>🔄 Adding more descriptive error messages</li>
      <li>🔄 Conducting regular accessibility audits</li>
    </ul>
  </section>

  <section>
    <h2>Legal Compliance</h2>
    <p>
      We strive to comply with:
    </p>
    <ul>
      <li><strong>WCAG 2.1 Level AA</strong> - Web Content Accessibility Guidelines</li>
      <li><strong>Section 508</strong> - US Federal accessibility standards</li>
      <li><strong>ADA Title III</strong> - Americans with Disabilities Act</li>
      <li><strong>EN 301 549</strong> - European accessibility standard</li>
      <li><strong>AODA</strong> - Accessibility for Ontarians with Disabilities Act (Canada)</li>
    </ul>
  </section>

  <section>
    <h2>Changes to This Statement</h2>
    <p>
      We may update this Accessibility Statement from time to time to reflect changes in our accessibility practices or standards.
      The "Last Updated" date at the top of this page shows when the statement was last revised.
    </p>
  </section>
</div>

<style>
  .legal-page {
    max-width: 760px;
    margin: 0 auto;
    padding: 2rem 1.5rem 4rem;
    color: hsl(var(--theme-foreground));
  }
  h1 {
    font-size: 1.75rem;
    font-weight: 700;
    margin-bottom: 0.375rem;
    letter-spacing: -0.02em;
    color: hsl(var(--theme-foreground));
  }
  .last-updated {
    color: hsl(var(--theme-muted-foreground));
    font-size: 0.8125rem;
    margin-bottom: 2.5rem;
  }
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-top: 2.5rem;
    margin-bottom: 0.75rem;
    color: hsl(var(--theme-foreground));
    border-bottom: 1px solid hsl(var(--theme-border) / 0.3);
    padding-bottom: 0.5rem;
    letter-spacing: -0.01em;
  }
  h3 {
    font-size: 1rem;
    font-weight: 600;
    margin-top: 1.5rem;
    margin-bottom: 0.5rem;
    color: hsl(var(--theme-foreground));
  }
  section { margin-bottom: 1.5rem; }
  p {
    font-size: 0.9375rem;
    line-height: 1.7;
    margin-bottom: 0.875rem;
    color: hsl(var(--theme-muted-foreground));
  }
  ul {
    margin: 0.75rem 0;
    padding-left: 1.25rem;
    line-height: 1.7;
  }
  li {
    margin-bottom: 0.375rem;
    font-size: 0.9375rem;
    color: hsl(var(--theme-muted-foreground));
  }
  strong { font-weight: 600; color: hsl(var(--theme-foreground)); }
  kbd {
    background: hsl(var(--theme-muted) / 0.2);
    border: 1px solid hsl(var(--theme-border));
    border-radius: 6px;
    padding: 0.125rem 0.4rem;
    font-family: 'Inter', monospace;
    font-size: 0.8125rem;
    color: hsl(var(--theme-foreground));
  }
  a {
    color: hsl(var(--theme-primary));
    text-decoration: none;
    border-bottom: 1px solid hsl(var(--theme-primary) / 0.3);
    transition: border-color 200ms ease;
  }
  a:hover { border-color: hsl(var(--theme-primary)); }
  @media (max-width: 640px) {
    .legal-page { padding: 1.5rem 1rem 3rem; }
    h1 { font-size: 1.5rem; }
    h2 { font-size: 1.125rem; }
  }
</style>
